<script>
    import $device from '../../global/device';
    import $util from '../../global/util';
    import drag from '../private/drag';
    import ibTableCol from './ib-table-col.vue';
    import ibTableColgroup from './ib-table-colgroup';
    import ibVisibility from '../misc/ib-visibility.vue';

    const rootMargin = 240;
    const inertia = 2;
    /* Minimum time in millseconds between table sort operations on data update */
    const sortThrottle = 3000;
    const ariaHidden = { 'aria-hidden': 'true' };


    /**
     * A responsive table with sorting and filtering.
     *
     * Note: Generally, please avoid use of flexbox within a table. Do not modify the `display` CSS property of table
     * cells; their display value is `table-cell`. Flexbox containers within a table cell will also not vertically
     * align properly relative to content in adjacent cells.
     */
    export default {
        name: 'ib-table-basic',

        mixins: [
            drag
        ],

        props: {
            /**
             * An array of objects containing data used to render each row. Presentation is
             * dependent on the "body" scoped slot.
             */
            data: {
                type: Array,
                default: () => []
            },
            /**
             * Member of data object to use as emitted row ID, or function to retrieve such.
             * If you provide this as a function, **DO NOT** inline it into your template.
             */
            emit: [String,Number,Function],
            /**
             * DOM `id` attribute.
             */
            id: String,
            /**
             * Force this table's height.
             */
            height: String,
            /**
             * Force this table's maximum height.
             */
            maxHeight: String,
            /**
             * Row density (spacing). Supported are `0`, `2`, `4`, `8`, `compact`, `comfortable`, and `huge`.
             */
            spacing: String,
            /**
             * Column density (spacing). Supported are `0`, `2`, and `4`. Default is `8`.
             */
            density: String,
            /**
             * Padding at left and right ends of table. Supported are `0`, `4`, and `8`. Default is `16`.
             */
            inset: String,
            /**
             * If true, allow text to wrap. By default, wrapping is disabled.
             */
            wrap: Boolean,
            /**
             * If true, use `table-layout: auto`. Table columns will be dynamically sized to content.
             * `ib-table-col` can be omitted if this is used.
             */
            auto: Boolean,
            /**
             * If true, the table is treated as an `ib-row`.
             */
            row: Boolean,
            /**
             * Filter rows so only those containing the given string are visible.
             */
            filter: String,
            /**
             * Appearance modifier; `rounded` is supported.
             */
            appearance: String,
            /**
             * Header modifier; `transparent` is supported.
             */
            header: String,
            /**
             * Body modifier; `transparent` is supported.
             */
            body: String,
            /**
             * Footer modifier; `transparent` is supported.
             */
            footer: String,
            /**
             * If true, use zebra striping on alternating rows.
             */
            rowstripe: Boolean,
            /**
             * If true, display a hairline border between rows.
             */
            rowborder: {
                type: Boolean,
                default: true
            },
            /**
             * If true, display a hairline border between columns.
             */
            colborder: Boolean,
            /**
             * Click action. `end` is supported to open end slot; default is no action.
             */
            click: {
                type: String,
                default: 'none'
            },
            /**
             * Drag action. `end` is supported to open end slot; default is scroll.
             */
            drag: String,
            /**
             * If `show` or `hide` events are used, selector for Scrolling element which we are monitoring row
             * visibility with respect to, or `"window"` for the `window`. By default, the table container will be
             * monitored.
             */
            root: String,
            /**
             * CSS margin to extend or reduce the visibility trigger area. This is only taken into account on browsers
             * that support the `IntersectionObserver` API.
             */
            rootMargin: String
        },

        data() {
            /** The following properties are nonreactive:
             *  indexCache
             *  indexNext
             *  indexto
             */
            return {
                col: [],
                colhover: -1,
                ready: false,
                dialog: false,
                headless: false,
                slotStart: false,
                slotEnd: false,
                stickyStart: 0,
                stickyEnd: 0,
                shadowStart: -1,
                shadowEnd: -1,
                sort: {},
                indexTrigger: false,
                filterfn: [],
                lastWidth: null,
                lastHeight: null,
                ulid: null    // uloff is nonreactive
            }
        },

        computed: {
            _id() {
                return this.id || '_tb' + this._uid;
            },

            _class() {
                const slot = this.slotStart || this.slotEnd;
                return {
                    _row: this.row,
                    _tbcl: this.lclick,
                    _tbrs: this.rowstripe,
                    _tbrb: this.rowborder,
                    _tbcb: this.colborder,
                    _tbwr: this.wrap,
                    _tbaut: this.auto,
                    _tbd: this.dialog,
                    _tbs: slot,
                    _tbu: this.drag == 'end',
                    _ltr: this.header && this.dir > 0,
                    _rtl: this.dir < 0,
                    ['_tbs'+this.spacing]: this.spacing,
                    ['_tbi'+this.inset]: this.inset,
                    ['_tbd'+this.density]: this.density,
                    ['_tbe'+this.appearance]: this.appearance,
                    ['_tbh'+this.header]: this.header,
                    ['_tbb'+this.body]: this.body,
                    ['_tbf'+this.footer]: this.footer
                }
            },

            _on() {
                return {
                    scroll: this.scroll,
                    touchstart: this.touchstart,
                    ...(this.lclick || this.handleEnd ? {
                        click: this.onclick,
                        mouseover: this.mouseover,
                        focusin: this.focusin,
                        focusout: this.focusout,
                        keydown: this.keydown
                    } : {})
                };
            },

            dir() {
                return this.$rtl ? -1 : 1;
            },

            lclick() {
                return this.click != 'none' || !!this.$listeners.click;
            },

            getid() {
                const emit = this.emit;
                if (typeof(emit) === 'function') {
                    return emit;
                } else if (emit != null) {
                    return row => row[emit];
                } else {
                    return (row,index) => index;
                }
            },

            getdisabled() {
                const disabled = this.disabled;
                if (disabled instanceof Set) {
                    return id => disabled.has(id);
                } else if (Array.isArray(disabled)) {
                    const set = new Set(disabled);
                    return id => set.has(id);
                } else {
                    const value = disabled === true;
                    return id => value;
                }
            },

            tbsStart() {
                return this.dir < 0 ? '_tbsr' : '_tbsl';
            },

            tbsEnd() {
                return this.dir < 0 ? '_tbsl' : '_tbsr';
            },

            handleEnd() {
                return this.slotEnd && $device.mouse;
            },

            sp() {
                return $util.scrollProps(this.$rtl);
            },

            collator() {
                return new Intl.Collator('en-US', {
                    numeric: true,
                    sensitivity: 'base'
                });
            },

            indexMap() {
                const getid = this.getid;
                return (row, index) => ({ index, id: getid(row, index) });
            },

            index() {
                void this.indexTrigger;
                const sortfn = this.sort.col?.sortfn;
                const data = this.data;
                if (sortfn) {
                    const now = Date.now();
                    if (now < this.indexNext) {
                        if (!this.indexto) {
                            this.indexto = setTimeout(() => {
                                this.indexTrigger = !this.indexTrigger;
                                this.indexto = 0;
                            }, this.indexNext - now);
                        }
                        return this.indexCache;
                    } else {
                        /**
                         * Sort data by values retrieved via `sortfn`. Sort order is:
                         * 1. If either value is null or an empty string, that row comes later
                         * 2. If both values are Numbers, they are compared directly.
                         * 3. Otherwise, numeric collation ("1" < "2" < "10") is used.
                         */
                        const collator = this.collator;
                        const dir = this.sort.dir;
                        const index = data
                            .map(this.indexMap)
                            .sort((a, b) => {
                                a = sortfn(data[a.index]);
                                b = sortfn(data[b.index]);
                                return (
                                      a == null || a === '' ? 1
                                    : b == null || a === '' ? -1
                                    : typeof a === 'number' && typeof b === 'number' ? dir * (a - b)
                                    : dir * collator.compare(a, b)
                                );
                            });
                        this.indexNext = now + sortThrottle;
                        return (this.indexCache = index);
                    }
                } else {
                    return data.map(this.indexMap);
                }
            },

            /**
             * Object with keys equal to table row IDs and values equal to `true` if that row
             * should be hidden with the current filter.
             */
            hidden() {
                let r = {};
                if (this.filter) {
                    const filter = RegExp(this.filter.replace(/[\\^$.|?*+()[{]/g, '\\$&'), 'i');
                    const data = this.data;
                    this.index.forEach(row => {
                        if (!this.filterfn.some(fn => filter.test(fn(data[row.index])))) {
                            r[row.id] = true;
                        }
                    });
                }
                return r;
            },

            renderRow() {
                const ulid = this.ulid;
                const udrag = this.udrag;
                const inplace = this.uip;
                const tabindex = this.lclick || this.handleEnd ? 0 : void 0;

                const tr = (h,id,children,cls) => h('tr', {
                    'key': id,
                    'class': {
                        _trh: id == this.hover,
                        _tro: id == ulid,
                        _tui: id == ulid && inplace,
                        _trs: id == ulid && udrag,
                        ...cls
                    },
                    'style': this.hidden[id] ? 'display:none' : id == ulid && !inplace ? 'transform:translateX(' + -this.uloff + 'px)' : null,
                    'attrs': {
                        'data-id': id,
                        'tabindex': tabindex
                    }
                }, children);

                return this.$listeners.enter || this.$listeners.leave
                    ? (h,id,children,cls) => h(ibVisibility, {
                        attrs: {
                            data: id,
                            root: this.root || this.$el,
                            rootMargin: this.rootMargin
                        },
                        on: {
                            show: this.rowEnter,
                            hide: this.rowLeave
                        }
                    }, [
                        tr(h, id, children, cls)
                    ])
                    : tr;
            }
        },

        watch: {
            sort(to) {
                this.indexto && clearTimeout(this.indexto);
                this.indexto = 0;
                this.indexNext = 0;
                /**
                 * Emits a reference to a column if we begin sorting by that column or change sort direction.
                 * This event is emitted when the table is mounted if a column is marked as sorted.
                 * @event sort
                 * @type {ib-table-col}
                 */
                this.$emit('sort', this.sort.col);
            },

            data(to, from) {
                if (to == null || from == null || to.length !== from.length) {
                    this.indexto && clearTimeout(this.indexto);
                    this.indexto = 0;
                    this.indexNext = 0;
                }
            },

            colhover(to, from) {
                const classList = this.$refs.table.classList;
                if (from >= 0) {
                    classList.remove('_sh' + (from + 1));
                }
                if (to >= 0) {
                    classList.add('_sh' + (to + 1));
                }
            },

            stickyStart(to) {
                if (to) {
                    this.scroll();
                }
            },

            stickyEnd(to) {
                if (to) {
                    this.scroll();
                }
            }
        },

        created() {
            this.parseNodes();
            this.ready = false;
            // csv is declared in the created hook to be used as a non-reactive variable
            // csv must be non-reactive to avoid infinite render loop
            this.csv = '';
        },

        mounted() {
            if (this.slotEnd != (!!this.$slots.end || this.$scopedSlots.end)) {
                this.parseNodes();
                this.$nextTick(() => {
                    const colgroup = this.$refs.colgroup;
                    if (colgroup) {
                        colgroup.refresh();
                    }
                });
            } else {
                this.$refs.colgroup.refresh();
                this.ready = true;
            }
            let parent = this.$parent;
            while (parent) {
                if (parent.isProxy_) {
                    this.dialog = true;
                    break;
                }
                parent = parent.$parent;
            }
        },

        updated() {
            this.scroll();

            // hover bypasses vue to apply class for performance; make sure it is in sync
            if (this.hover) {
                const hovertr = this.$el.querySelector('tr[data-id="' + this.hover.replace(/["\\]/g, '\\$&') + '"]');
                if (this.hovertr !== hovertr) {
                    if (this.hovertr) {
                        this.hovertr.classList.remove('_trh');
                    }
                    this.hovertr = hovertr;
                }
            }
        },

        beforeDestroy() {
            this.ready = false;
            this.indexto && clearTimeout(this.indexto);
        },

        render(h) {
            const headStart = this.slotStart
                ? []
                : [
                    h(ibTableCol, {
                        'class': this.tbsStart,
                        'attrs': { width: '0', hidden: true }}) ];

            const headEnd = this.slotEnd
                ? [
                    h(ibTableCol, {
                        'class': this.tbsEnd,
                        'attrs': { width: this.handleEnd ? '24px' : '0', force: true }}),
                    h(ibTableCol, {
                        'class': this.tbsEnd + 'e',
                        'attrs': { width: '0' }}) ]
                : [
                    h(ibTableCol, {
                        'class': this.tbsEnd,
                        'attrs': { width: '0', hidden: true }}) ];

            const footEnd = this.slotEnd
                ? [
                    h('td', {
                        'class': this.tbsEnd,
                        'attrs': ariaHidden }),
                    h('div', {
                        'class': this.tbsEnd,
                        'attrs': ariaHidden }) ]
                : [
                    h('div', {
                        'class': this.tbsEnd,
                        'attrs': ariaHidden }) ];

            return h('div', {
                'staticClass': '_tbw _con',
                'class': this._class,
                'style': { height: this.height, maxHeight: this.maxHeight },
                'on': this._on,
                'directives': [
                    {
                        name: 'size',
                        rawName: 'v-size',
                        value: this.resize,
                        expression: 'size',
                        modifiers: {
                            width: true
                        }
                    }
                ]
            }, [
                ...this.renderBefore(h),
                h('table', {
                    'ref': 'table',
                    'staticClass': '_tb',
                    'attrs': { id: this._id }
                }, [
                    h(ibTableColgroup, {
                        'ref': 'colgroup',
                        'attrs': {
                            col: this.col,
                            fixed: this.drag == 'end'
                        }
                    }),
                    this.$slots.caption ? h('caption', this.$slots.caption) : void 0,
                    h('thead', {
                        'staticClass': '_tbh'
                    }, [
                        this.$slots.head ? h('tr', [
                            h('div', {
                                'class': this.tbsStart,
                                'attrs': ariaHidden
                            }),
                            this.$slots.head,
                            h('div', {
                                'class': this.tbsEnd,
                                'attrs': ariaHidden
                            })
                        ]) : void 0,
                        h('tr', {
                            'style': this.headless ? 'display:none' : null
                        }, [
                            ...headStart,
                            ...this.renderHeadBefore(h),
                            this.$slots.default,
                            ...this.renderHeadAfter(h),
                            ...headEnd
                        ])
                    ]),
                    ...[ this.renderBody(h) ],
                    this.$slots.foot ? h('tfoot', {
                        'staticClass': '_tbf'
                    }, [
                        h('tr', [
                            h('div', {
                                'class': this.tbsStart,
                                'attrs': ariaHidden
                            }),
                            this.$slots.foot,
                            ...footEnd
                        ])
                    ]) : void 0
                ])
            ]);
        },

        methods: {
            /**
             * return sorted data from the table.
             */
            getSortedData() {
                const data = this.data;
                return this.index.map(row => data[row.index]);
            },

            /**
             * download CSV file.
             * Expected use is to be called externally through Vue refs.
             * Header csv & data csv strings can optionally be passed in or data will default data added with slotScope:body add csv function.
             */

            downloadCsvFile(fileName, headerCsv, dataCsv) {
                if (!fileName) {
                    console.log('ib-table-basic downloadCsvFile({file name}, {headerCsv}, {dataCsv}) requires a file name parameter');
                    return;
                }
                if ((headerCsv && typeof headerCsv !== 'string') || (dataCsv && typeof dataCsv !== 'string')) {
                    console.log('ib-table-basic downloadCsvFile({file name}, {headerCsv}, {dataCsv}) headerCsv & dataCsv parameters must be type string');
                    return;
                }

                let a = window.document.createElement('a'),
                    name = fileName,
                    csvHeaders = headerCsv ? headerCsv + '\n': '',
                    csvBody = dataCsv ? dataCsv : this.getCsv(),
                    csv = csvHeaders + csvBody,
                    csvBlob = this.createCsvFileBlob(csv);

                a.download = name;
                a.href = window.URL.createObjectURL(csvBlob, {
                    type: 'text/plain'
                });

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            },

            setCsv(value, modifier) {
                if (modifier === undefined) {
                    modifier = ', ';
                } else if (modifier.toLowerCase() === 'end') {
                    modifier = '\n';
                }

                this.csv += value + modifier;
                return value;
            },

            /**
             * can be called externally to fetch csv if download is not required.
             */
            getCsv() {
                return this.csv;
            },

            clearCsv() {
                this.csv = '';
            },

            /**
             * return blob with csv data.
             */
            createCsvFileBlob(csvData) {
                return new Blob([csvData], { type: 'plain/text' });
            },

            /**
             * Get row data corresponding to id.
             */
            getrow(id) {
                const row = this.index.find(row => row.id == id);
                return {
                    data: this.data[row.index],
                    id: row.id
                };
            },

            /**
             * Show the end slot for the row with the given id. If the event is on a touch only
             * device or was likely triggered by touch, slide the entire row.
             */
            showEnd(id) {
                this.uip = $device.mouse && !this.touched && !this.udrag;
                this.ulDragEnd();
                this.ulSet(id);
            },

            /**
             * Hide the end slot if the given id is currently open.
             */
            hideEnd(id) {
                if (this.ulid == id) {
                    this.uip = false;
                    this.ulDragEnd();
                    this.ulSet();
                }
            },

            /** @private */
            renderBefore(h) {
                return [];
            },

            /** @private */
            renderHeadBefore(h) {
                this.csvHeaders = '';
                return [];
            },

            /** @private */
            renderHeadAfter(h) {
                return [];
            },

            /** @private */
            renderBodyStart(h) {
                return [
                    h('div', {
                        'class': this.tbsStart,
                        'attrs': ariaHidden
                    })];
            },

            /** @private */
            renderBodyEnd(h, id, data, disabled) {
                let end;
                if (this.selfSlotEnd) {
                    end = this.renderSlotEnd(h, data, disabled);
                } else if (data && this.slotEnd) {
                    end = this.$slots.end || this.$scopedSlots.end(data);
                    if (end && !end.some(el => el.tag)) {
                        end = void 0;
                    }
                }
                const inplace = this.uip;
                const ulid = this.ulid;
                return end
                    ? [
                        h('td', id == ulid ? {
                            'class': this.tbsEnd + ' _tbe',
                            'style': 'transform:translateX(' + this.uloff + 'px)'
                        } : {
                            'class': this.tbsEnd
                        }, this.handleEnd ? [
                            h('button', {
                                'class': '_tbsrc',
                                'staticClass': '_btn clear',
                                'attrs': {
                                    'tabindex': -1
                                },
                                'on': {
                                    'click': this.slotEndClick
                                }
                            }, [
                                h('svg', { attrs: { viewBox: '0 0 4 16' } },[ h('path', { attrs: { d: 'M4 2a2 2 0 10-2 2 2 2 0 002-2zm0 6a2 2 0 10-2 2 2 2 0 002-2zm0 6a2 2 0 10-2 2 2 2 0 002-2z' }}) ])
                            ])] : void 0),
                        h('td', {
                            'class': this.tbsEnd + 'e'
                        }, [
                            h('div', {
                                'class': this.dir < 0 ? '_tbld' : '_tbrd'
                            }, [
                                h('div', {
                                    'staticClass': '_btn-seg',
                                    'style': id == ulid && !inplace ? 'transform:translateX(' + this.uloff + 'px)' : null,
                                    'on': {
                                        'click': this.ulClose
                                    }
                                }, [
                                    end
                                ])
                            ])
                        ])]
                    : [
                        h('div', {
                            'class': this.tbsEnd,
                            'attrs': ariaHidden }),
                        this.slotEnd ? h('div', {
                            'class': this.tbsEnd + 'e'
                        }) : void 0
                    ];
            },

            /** @private */
            renderSlot(name, fallback) {
                return this.$scopedSlots[name] || (this.$slots[name] ? () => this.$slots[name] : fallback || (data => data.map(e => h('td', [e]))));
            },

            /** @private */
            renderBody(h) {
                const data = this.data;
                const body = this.renderSlot('body');
                const renderRow = this.renderRow;

                this.clearCsv();

                return h('tbody', {
                        'staticClass': '_tbb'
                    },
                    this.index.length
                        ? this._l(this.index, row => {
                            const rdata = data[row.index];
                            const id = row.id;
                            return renderRow(h, id, [
                                ...this.renderBodyStart(h),
                                body(rdata, this.setCsv),
                                ...this.renderBodyEnd(h, id, rdata)
                            ]);
                        })
                        : this.renderEmpty(h));
            },

            /** @private */
            renderEmpty(h) {
                return [
                    h('tr', [
                        h('td', {
                            'attrs': { colspan: this.col.length }
                        }, [
                            ...this.renderBodyStart(h),
                            this.$slots.empty,
                            ...this.renderBodyEnd(h)
                        ])
                    ])
                ];
            },

            /** @private */
            sync() {
                if (this.ready) {
                    this.ready = false;
                    this.$nextTick(this.parseNodes);
                }
            },

            /** @private */
            parseNodes() {
                this.slotStart = !!this.$slots.start || !!this.$scopedSlots.start || this.selfSlotStart;
                this.slotEnd = !!this.$slots.end || !!this.$scopedSlots.end || this.selfSlotEnd;
                const col = (this.$slots.default || []).filter(vnode =>
                    vnode.componentOptions &&
                    vnode.componentOptions.Ctor.options.name === 'ib-table-col');
                this.col = [
                    ...this.before(),
                    ...col.map(vnode => vnode.componentOptions.propsData),
                    ...this.after()
                ];
                if (this.sort.col && !col.includes(this.sort.col.$vnode)) {
                    this.sort = {};
                }
                this.headless = !col.some(vnode => vnode.componentOptions.children);
                this.filterfn = col.filter(vnode => vnode.componentOptions.propsData.filter != null).map(vnode => {
                    const filter = vnode.componentOptions.propsData.filter;
                    return typeof filter === 'function' ? filter : row => row[filter];
                });
                this.ready = true;
            },

            /** @private */
            before() {
                return [{ width: 0 }];
            },

            /** @private */
            after() {
                return (this.slotEnd ? [{ width: this.handleEnd ? '24px' : 0, sticky: 'end', align: 'end', force: true }] : []).concat([{ width: 0 }]);
            },

            /** @private */
            resize(width) {
                if (this.lastWidth !== width) {
                    this.lastWidth = width;
                    this.$refs.colgroup.dropUpdate(this.$el.offsetWidth);
                    this.scroll();
                }
            },

            /** @private */
            scroll(e) {
                if (this.drag != 'end') {
                    const el = this.$el;
                    const start = Math.min(16, Math.max(0, this.sp.base * (el.scrollWidth - el.offsetWidth) + this.sp.step * el.scrollLeft) / 16 | 0);
                    if (this.shadowStart != start || e === undefined) {
                        const table = this.$refs.table;
                        table.classList.remove(this.tbsStart + this.shadowStart);
                        if (start) {
                            table.classList.add(this.tbsStart + start);
                        }
                        this.shadowStart = start;
                    }
                    const end = Math.min(16, Math.max(0, (1-this.sp.base) * (el.scrollWidth - el.offsetWidth) - this.sp.step * el.scrollLeft) / 16 | 0);
                    if (this.shadowEnd != end || e === undefined) {
                        const table = this.$refs.table;
                        table.classList.remove(this.tbsEnd + this.shadowEnd);
                        if (end) {
                            table.classList.add(this.tbsEnd + end);
                        }
                        this.shadowEnd = end;
                    }
                }
            },

            /** @private */
            rowEnter(id) {
                /**
                 * Emits a row ID that has just entered the viewport. This is useful for lazy loading data.
                 * @event enter
                 * @type {Array}
                 */
                this.$emit('enter', id);
            },

            /** @private */
            rowLeave(id) {
                /**
                 * Emits a row ID that has just left the viewport. This is useful for lazy loading data.
                 * @event leave
                 * @type {Array}
                 */
                this.$emit('leave', id);
            },

            /** @private */
            td(e) {
                return e.target.closest('td');
            },

            /** @private */
            tr(td) {
                return td ? td.closest('tr') : null;
            },

            /** @private */
            mouseover(e) {
                if (!this.touched) {
                    const tr = this.hoverhook(e.target);
                    if (tr) {
                        tr.addEventListener('mouseleave', this.mouseleave);
                    }
                }
            },

            /** @private */
            focusin(e) {
                this.hoverhook(e.target, e.timeStamp < __app.tabts);
            },

            /** @private */
            focusout(e) {
                if (e.target === this.hovertr) {
                    this.hoverdone();
                }
            },

            /** @private */
            mouseleave(e) {
                this.hoverdone();
                e.target.removeEventListener('mouseleave', this.mouseleave);
            },

            /** @private */
            touchstart(e) {
                if (!this.disabled && e.which == 0) {
                    if (!this.lclick || this.hoverhook(e.target)) {
                        document.addEventListener('touchmove', this.touchend, false);
                        document.addEventListener('touchend', this.touchend, false);
                        if (this.drag == 'end') {
                            this.ulDragStart(e);
                        }
                    }
                }
            },

            /** @private */
            touchend(e) {
                if (e.which == 0) {
                    this.hoverdone();
                    document.removeEventListener('touchmove', this.touchend, false);
                    document.removeEventListener('touchend', this.touchend, false);
                    // if a click event occurs within 500ms of touchend, we assume it was the result of touch
                    if (this.touched) {
                        clearTimeout(this.touched);
                    }
                    this.touched = setTimeout(() => { this.touched = 0; }, 500);
                }
            },

            /** @private */
            onclick(e, action) {
                if (!this.udrag && !e.button && (action == 'end' || e.target.closest('button') == null)) {
                    const td = this.td(e);
                    const tr = this.tr(td);
                    if (tr) {
                        this.trigger(tr, td, action);
                    }
                }
            },

            /** @private */
            keydown(e) {
                if (e.which == 13 && e.target.tagName == 'TR') {
                    this.trigger(e.target);
                }
            },

            /** @private */
            trigger(tr, td, action) {
                const id = tr.getAttribute('data-id');
                if (id != null) {
                    if ((action || this.click) == 'end' && this.slotEnd) {
                        if (this.uloff && id == this.ulid) {
                            this.hideEnd(id);
                        } else {
                            this.showEnd(id);
                        }
                    } else {
                        this.clickhook(id, action);
                        if (!action) {
                            this.clickemit(id, [].indexOf.call(tr.children, td));
                        }
                    }
                }
            },

            /** @private */
            canhover(id) {
                return true;
            },

            /** @private */
            hoverhook(el, fvis) {
                const tr = this.tr(el);
                if (tr) {
                    const id = tr.getAttribute('data-id');
                    if (id != null) {
                        this.hoverdone();
                        if (this.canhover(id)) {
                            tr.classList.add('_trh');
                            this.hover = id;
                            this.hovertr = tr;
                            if (fvis) {
                                tr.classList.add('_fvis');
                                this.fvis = true;
                            }
                            return tr;
                        }
                    }
                }
            },

            hoverdone() {
                const hovertr = this.hovertr;
                this.hover = null;
                this.hovertr = null;
                if (hovertr) {
                    hovertr.classList.remove('_trh');
                    if (this.fvis) {
                        this.fvis = false;
                        hovertr.classList.remove('_fvis');
                    }
                }
            },

            /** @private */
            clickhook() {
                // empty
            },

            /** @private */
            clickemit(id, ci) {
                const ri = this.index.findIndex(row => row.id == id);
                if (ri >= 0) {
                    /**
                     * Emits the row data and the associated column component when a table cell is clicked.
                     * @event click
                     * @type {object}
                     */
                    this.$emit('click', {
                        row: ri >= 0 ? this.data[this.index[ri].index] : void 0,
                        col: ci >= 0 ? this.col[ci] : void 0,
                        index: ri
                    });
                }
            },

            /**
             * Set horizontal position of sliding row.
             * @private
             */
            ulPos(tr, td, x) {
                if (this.uip) {
                    const handle = td.parentNode.previousSibling;
                    if (handle) {
                        handle.style.transform = 'translateX(' + x + 'px)';
                    }
                } else {
                    tr.style.transform = 'translateX(' + -x + 'px)';
                    td.style.transform = 'translateX(' + x + 'px)';
                }
            },

            /**
             * Open or close sliding row.
             * @private
             */
            ulSet(id) {
                this.ulid = id;
                if (id != null) {
                    document.addEventListener('touchstart', this.ulCancel, true);
                    document.addEventListener('mousedown', this.ulCancel, true);
                    const tr = this.$el.querySelector('tr[data-id="' + id.replace(/["\\]/g, '\\$&') + '"]');
                    if (tr) {
                        const td = tr.querySelector('._btn-seg');
                        if (td) {
                            this.uloff = this.dir * (this.uip ? -1 : 1) * td.getBoundingClientRect().width | 0;
                            this.ulPos(tr, td, this.uloff);
                        }
                    }
                } else {
                    document.removeEventListener('touchstart', this.ulCancel, true);
                    document.removeEventListener('mousedown', this.ulCancel, true);
                }
            },

            /**
             * Close sliding row on body click.
             * @private
             */
            ulCancel(e) {
                if (!e.button) {
                    const tr = this.tr(e.target);
                    if (!tr || !tr.classList.contains('_tro')) {
                        this.ulSet();
                    }
                }
            },

            /**
             * Close sliding row on button click.
             * @private
             */
            ulClose(e) {
                if (!e.button && e.target.closest('button')) {
                    this.ulSet();
                }
            },

            /**
             * Sliding row drag init. This is also called prior to click.
             * @private
             */
            ulDragStart(e) {
                const tr = this.tr(e.target);
                const id = tr.getAttribute('data-id');
                if (id != null) {
                    const td = tr.querySelector('._btn-seg');
                    if (td && !td.contains(e.target)) {
                        if (this.utt) {
                            clearTimeout(this.utt);
                            this.utt = 0;
                        }
                        if (this.index[+index].id == this.ulid) {
                            this.ulini =
                            this.ulmax = this.dir * this.uloff;
                        } else {
                            this.ulSet(this.index[+index].id);
                            this.ulmax = this.dir * this.uloff;
                            this.ulini = 0;
                            this.uloff = 0;
                        }
                        this.utr = tr;
                        this.utd = td;
                        this._dragcapture(tr, e);
                    }
                }
            },

            /**
             * Sliding row drag finish or abort.
             * @private
             */
            ulDragEnd() {
                if (this.utr) {
                    this.utr.classList.remove('_trs');
                }
                this.utr =
                this.utd = null;
                this.utt = setTimeout(() => {
                    this.udrag = false;
                    this.utt = 0;
                }, 100);
            },

            /**
             * Sliding row drag move.
             * @private
             */
            _dragmove(x) {
                if (!this.udrag) {
                    this.udrag = true;
                    this.utr.classList.add('_trs');
                }
                const dir = this.dir;
                const ulmax = this.ulmax;
                x = -dir * x + this.ulini;
                if (x > ulmax) {
                    x = ulmax + (x - ulmax) / Math.log10(1 + x - ulmax);
                } else if (x < 0) {
                    x = 0;
                    //x /= Math.log10(1 - x);
                }
                // uloff is nonreactive
                this.ulPos(this.utr, this.utd, this.uloff = dir * x);
            },

            /**
             * Sliding row drag release.
             * @private
             */
            _dragup(x) {
                if (this.udrag) {
                    const dir = this.dir;
                    const duration = Date.now() - this.uts;
                    if (duration > 20) {
                        x += x * Math.abs(x * inertia / duration);
                    }
                    x = -dir * x + this.ulini;
                    const pos = x / this.ulmax;
                    if (pos > .5) {
                        this.ulPos(this.utr, this.utd, this.uloff = this.dir * this.ulmax);
                    } else {
                        this.ulSet();
                    }
                    this.ulDragEnd();
                } else if (this.uloff) {
                    this.hideEnd(this.ulid);
                } else {
               //     this.showEnd(this.ulid);
                }
            },

            /**
             * Click on kebab handle button
             */
            slotEndClick(e) {
                const button = e.target.closest('button');
                if (button) {
                    button.blur();
                }
                this.onclick(e, 'end');
                e.stopPropagation();
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    @tr-padx: 16px;
    @tr-padx-sm: 8px;
    @tr-padx-lg: 24px;
    @tr-padx-xl: 32px;
    @th-pad: 3px 8px;
    @td-pad: 8px;
    @th-color: fade(@fg, 46%);

    @bg-highlight-alt: mix(@fg-max, @bg-highlight, 2%);

    ._tbw {
        --highlight1: @bg-highlight;
        --highlight2: @bg-highlight-alt;
        & {
            --bc: @border-default;
        }
        @media (-webkit-min-device-pixel-ratio: 3), (min-resolution: 3dppx) {
            --bc: @border-default-hi;
        }

        position: relative;
        color: @fg;
        white-space: nowrap;
        overflow: auto !important;
        .font-size(@fs6);
        .line-height(round(@fs6 * 1.125 + 2));
        -webkit-overflow-scrolling: touch;
        > .progress {
            top: calc(10rem / @font-base ~' + 14px');
        }
    }
    ._tbu {
        overflow-x: hidden;
        tbody tr {
            touch-action: pan-y;
            -webkit-tap-highlight-color: transparent;
        }
    }
    ._tb {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        table-layout: fixed;

        col.hide {
            visibility: collapse;
            width: 0 !important;
            border: 0;
        }
        thead {
            z-index: 3;
        }
        caption,
        th {
            .font-size(@fs8);
            line-height: 2;
            font-weight: 500;
            color: @th-color;
            padding: @th-pad;

            > ._thc {
                display: inline-block;
                vertical-align: bottom;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            > ._tha svg {
                width: 15px;
                height: 15px;
                opacity: .4;
                vertical-align: -6px;
            }
        }
        th.sort {
            > ._thc {
                max-width: calc(~'100% - 16px');
            }
        }
        th {
            position: -webkit-sticky;
            position: sticky;
            top: 0;
            cursor: default;
            z-index: 2;
            text-align: start;
            .table-th();
            &.sort {
                cursor: pointer;
            }
            &.sorted {
                > ._tha svg {
                    opacity: 1;
                }
            }
        }
        td {
            position: relative;
            padding: @td-pad;
            vertical-align: baseline;
            text-overflow: ellipsis;
            overflow: hidden;
            z-index: 0;
        }

        /** Start/end padding */
        th:first-of-type,
        td:first-of-type {
            -webkit-padding-start: @tr-padx;
            padding-inline-start: @tr-padx;
        }
        th:last-of-type,
        td:last-of-type {
            -webkit-padding-end: @tr-padx;
            padding-inline-end: @tr-padx;
        }
    }

    /** Background and border colors */
    ._tb {
        th, td {
            background-color: @bg;
        }
    }

    /** End cell */
    ._tb {
        ._tbsl,
        ._tbsr {
            display: table-cell;
            position: -webkit-sticky;
            position: sticky;
            padding: 0 !important;
            overflow: visible;
            z-index: 4;
        }
        ._tbsrc {
            position: absolute;
            visibility: hidden;
            margin: 0;
            padding: 0;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            max-width: none;
            font-size: 14px;
            border-radius: 0;
            color: fade(@fg-accent, 70%);
        }
    }
    ._tbsl::before,
    ._tbsr::before {
        opacity: 0;
    }
    ._tbsl::before,
    ._tbsr::before,
    ._tbsl._tbe::after,
    ._tbsr._tbe::after {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 24px;
        pointer-events: none;
    }
    ._tbsl::before,
    ._tbsr._tbe::after {
        left: 100%;
        background:
            linear-gradient(90deg, @table-shadow 0, transparent 100%),
            linear-gradient(90deg, @table-shadow 0, transparent 50%),
            linear-gradient(90deg, @table-shadow 0, transparent 25%);
    }
    ._tbsr::before,
    ._tbsl._tbe::after {
        right: 100%;
        background:
            linear-gradient(270deg, @table-shadow 0, transparent 100%),
            linear-gradient(270deg, @table-shadow 0, transparent 50%),
            linear-gradient(270deg, @table-shadow 0, transparent 25%);
    }
    ._tbh ._tbsl::before {
        background:
            radial-gradient(at bottom left, @table-shadow 0, transparent 100%),
            radial-gradient(at bottom left, @table-shadow 0, transparent 50%),
            radial-gradient(at bottom left, @table-shadow 0, transparent 25%);
    }
    ._tbh ._tbsr::before {
        background:
            radial-gradient(at bottom right, @table-shadow 0, transparent 100%),
            radial-gradient(at bottom right, @table-shadow 0, transparent 50%),
            radial-gradient(at bottom right, @table-shadow 0, transparent 25%);
    }

    /** Sliding underlay */
    ._tb {
        ._tbsls,
        ._tbsrs,
        ._tbsle,
        ._tbsre {
            position: -webkit-sticky;
            position: sticky;
            padding: 0 !important;
            z-index: -1;
            overflow: visible;
            transition: none;
        }
        ._tbsls,
        ._tbsle {
            left: 0;
        }
        ._tbsrs,
        ._tbsre {
            right: 0;
        }
        ._btn-seg {
            display: inline-flex;
            align-items: stretch;
            ._btn {
                max-width: none;
                height: 100%;
                margin: 0;
            }
        }
    }
    ._tbld,
    ._tbrd {
        position: absolute;
        display: inline-flex;
        top: 0;
        bottom: 0;
        visibility: hidden;
        overflow: hidden;
        background-color: @bg;
    }
    ._tbld {
        left: 0;
        ._btn-seg {
            transform: translateX(-100%);
        }
    }
    ._tbrd {
        right: 0;
        ._btn-seg {
            transform: translateX(100%);
        }
    }
    ._tui {
        ._tbld,
        ._tbrd {
            visibility: visible;
        }
        ._btn-seg {
            transform: none;
        }
    }

    /** Stretch button color for elastic drag; hacky but seems reliable */
    ._tro:not(._tui) {
        ._tbld,
        ._tbrd {
            overflow: visible;
        }
        ._btn-seg {
            > ._btn:first-child,
            > :first-child > ._btn:first-child {
                -webkit-margin-start: -512px;
                -webkit-padding-start: 512px + extract(@btn-padding, 2);
                margin-inline-start: -512px;
                padding-inline-start: 512px + extract(@btn-padding, 2);
            }
        }
    }
    ._trs {
        will-change: transform;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
    }
    ._tbs {
        tr,
        ._btn-seg {
            transition: visibility 0s linear .3s, transform .3s;
        }
        ._trs,
        ._trs ._btn-seg {
            transition: none;
        }
    }
    ._tro {
        ._btn-seg {
            visibility: visible;
            transition: transform .3s;
        }
    }

    /** In-place start/end slot; specific to mouse click event */
    ._tui {
        > td {
            background-color: @bg-highlight;
        }
        > ._tbsl,
        > ._tbsr {
            transition: transform .3s;
        }
        ._tbsls,
        ._tbsrs,
        ._tbsle,
        ._tbsre {
            z-index: 3;
        }
    }

    /** Sticky column shadow opacity */
    each(range(16), {
        ._tbsl@{value} ._tbsl::before,
        ._tbsr@{value} ._tbsr::before {
            content: '';
            opacity: (@value / 16);
            transition: opacity unit((@value / 32), s);
        }
    });

    /** Row stripe */
    ._tbrs {
        ._tbb > tr:nth-child(2n) {
            > td {
                background-color: mix(@fg-max, @bg, 2%);
            }
            .tbsrc,
            div._tbsr {
                background-color: @bg-highlight-alt;
            }
        }
    }

    /** Row border */
    ._tbrb td, ._tb th {
        border-bottom: 1px solid @border-default-lo;
    }
    ._tbrb tbody:last-child > tr:last-child > td,
    ._tbrb tfoot:last-child > tr:last-child > td {
        border-bottom: none;
        background-image: none;
    }

    /** Column border */
    ._tbcb td {
        border-right: 1px solid @border-default-lo;
    }

    /** Subpixel row and column borders */
    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx) {
        ._tbrb td, ._tb th {
            border-bottom: .5px solid var(--bc);
        }
        ._tbcb td {
            border-right: .5px solid var(--bc);
        }
    }


    /** Row click cursor */
    ._tbcl {
        tbody tr {
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
        }
        tbody tr._da {
            cursor: default;
            opacity: .5;
        }
    }

    /** Row hover */
    ._trh > td {
        background-color: @bg-highlight;
    }
    ._tbrs {
        ._tbb > ._trh:nth-child(2n) > td {
            background-color: @bg-highlight-alt;
        }
    }
    ._trh {
        div._tbsr {
            background-color: @bg-highlight;
        }
    }
    ._trh,
    ._tui {
        ._tbsrc {
            visibility: visible;
        }
    }

    /** Text wrap */
    ._tbwr {
        white-space: normal;
    }

    /** Auto column widths */
    ._tbaut > ._tb {
        table-layout: auto;
    }

    /** Spacing modifiers */
    ._tbw {
        &.wrap {
            white-space: normal;
        }
        &._tbs2 {
            th {
                padding-top: 0;
                padding-bottom: 0;
            }
            td {
                padding-top: 1px;
                padding-bottom: 1px;
            }
        }
        &._tbs4,
        &._tbscompact {
            th {
                padding-top: 0;
                padding-bottom: 0;
            }
            td {
                padding-top: 2px;
                padding-bottom: 2px;
            }
        }
        &._tbs8 {
            td {
                padding-top: 4px;
                padding-bottom: 4px;
            }
        }
        &._tbscomfortable {
            td {
                padding-top: 12px;
                padding-bottom: 12px;
            }
        }
        &._tbshuge {
            td {
                padding-top: 18px;
                padding-bottom: 18px;
            }
        }
        &._tbd0 {
            th, td {
                padding-left: 0;
                padding-right: 0;
            }
        }
        &._tbd2 {
            th, td {
                padding-left: 1px;
                padding-right: 1px;
            }
        }
        &._tbd4 {
            th, td {
                padding-left: 2px;
                padding-right: 2px;
            }
        }
        &._tbd8 {
            th, td {
                padding-left: 4px;
                padding-right: 4px;
            }
        }
        &._tbi0 {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: 0;
                padding-inline-start: 0;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: 0;
                padding-inline-end: 0;
            }
        }
        &._tbi4 {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: 4px;
                padding-inline-start: 4px;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: 4px;
                padding-inline-end: 4px;
            }
        }
        &._tbi8 {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: 8px;
                padding-inline-start: 8px;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: 8px;
                padding-inline-end: 8px;
            }
        }
        &._tbism {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-sm;
                padding-inline-start: @tr-padx-sm;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-sm;
                padding-inline-end: @tr-padx-sm;
            }
        }
        &._tbilg {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-lg;
                padding-inline-start: @tr-padx-lg;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-lg;
                padding-inline-end: @tr-padx-lg;
            }
        }
        &._tbixl {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-xl;
                padding-inline-start: @tr-padx-xl;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-xl;
                padding-inline-end: @tr-padx-xl;
            }
        }
    }

    /** Appearance modifiers */
    ._tberounded:not(._tbhtransparent) {
        border-radius: 8px;
    }
    ._tbhtransparent {
        th {
            position: static;
            background: none;
            .table-th-transparent();
        }
    }
    ._tbbtransparent {
        td {
            background: none;
        }
    }
    ._tberounded._tbhtransparent._ltr {
        caption,
        th {
            border-bottom: none;
        }
        tbody:first-of-type > tr:first-of-type {
            > td:first-of-type {
                border-top-left-radius: 8px;
            }
            > td:last-of-type {
                border-top-right-radius: 8px;
            }
        }
        tbody:last-of-type > tr:last-of-type {
            > td:first-of-type {
                border-bottom-left-radius: 8px;
            }
            > td:last-of-type {
                border-bottom-right-radius: 8px;
            }
        }
    }
    ._tberounded._tbhtransparent._rtl {
        tbody:first-of-type > tr:first-of-type {
            > td:first-of-type {
                border-top-right-radius: 8px;
            }
            > td:last-of-type {
                border-top-left-radius: 8px;
            }
        }
        tbody:last-of-type > tr:last-of-type {
            > td:first-of-type {
                border-bottom-right-radius: 8px;
            }
            > td:last-of-type {
                border-bottom-left-radius: 8px;
            }
        }
    }

    /** Form control style modifiers */
    ._tb {
        ._check {
            &:not(._edefault) > ._cbc {
                .checkbox-frameless();
            }
            &:not(._edefault) > input:checked + ._cbc {
                .checkbox-frameless-checked();
            }
        }
        ._radio {
            &:not(._edefault) > ._cbc {
                .radio-frameless();
            }
            &:not(._edefault) > input:checked + ._cbc {
                .radio-frameless-checked();
            }
        }
        ._toggle {
            margin: -4px 0;
        }
        ._field {
            &:not(._ebox):not(._eunderline) {
                .field-table();
                margin: -4px 0;
            }
        }
        ._drop {
            &:not(._ebox):not(._eunderline) {
                .dropdown-table();
                margin: -4px 0;
            }
        }
    }

    /** Color alternates for dialogs */
    & when not (@bg = @bg-dialog) {
        @bg-highlight-dialog-alt: mix(@fg-max, @bg-highlight-dialog, 2%);

        ._tbd {
            --highlight1: @bg-highlight-dialog;
            --highlight2: @bg-highlight-dialog-alt;

            th, td {
                background-color: @bg-dialog;
            }
            ._tui {
                > td {
                    background-color: @bg-highlight-dialog;
                }
            }
            ._trh > td {
                background-color: @bg-highlight-dialog;
            }
            ._tbrs {
                ._tbb > ._trh:nth-child(2n) > td {
                    background-color: @bg-highlight-dialog-alt;
                }
            }
            ._trh {
                div._tbsr {
                    background-color: @bg-highlight-dialog;
                }
            }
        }
    }
</style>

<ib-example id="sortdrop" name="Sorting, Droppable Column, Download CSV">
    <template lang="pug">
        div
            ib-table-basic(:data="positions", ref="dataTable")
                ib-table-col(width="12ch", sort="currency", sorted) Currency
                ib-table-col(width="32ch", drop) Note
                ib-table-col(width="12ch", sort="amount", numeric) Amount
                template(v-slot:body="pos, csv")
                    td(v-text="csv(pos.currency)")
                    td(v-text="csv(pos.note)")
                    td(v-num.fixed="csv(pos.amount, 'end')")
            ib-button(@click="downloadCsv" class="before-16") Download CSV
    </template>
    <script>
        {
            methods: {
                downloadCsv() {
                    this.$refs.dataTable.downloadCsvFile('csv', 'Currency, Note, Amount');
                }
            },
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ]
                };
            }
        }
    </script>
</ib-example>

<ib-example id="stripe" name="Zebra Striping, Column Border, Clickable Rows, Slide Buttons">
    <template lang="pug">
        ib-table-basic(:data="positions", rowstripe, colborder, @click="click")
            ib-table-col(width="12ch", sort="currency", sorted) Currency
            ib-table-col(width="32ch", drop) Note
            ib-table-col(width="12ch", sort="amount", numeric) Amount
            template(v-slot:body="pos")
                td(v-text="pos.currency")
                td(v-text="pos.note")
                td(v-num.fixed="pos.amount")
            template(v-slot:end="pos")
                template(v-if="pos.currency != 'USD'")
                    ib-button(tone="buy", @click="buy(pos.currency)") Buy
                    ib-button(tone="sell", @click="sell(pos.currency)") Sell
                    ib-button(@click="quote(pos.currency)") Quote
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ]
                };
            },
            methods: {
                click(data) {
                    this.quote(data.row.currency);
                },

                buy(currency) {
                    this.$notify(this.$t('Buy {currency}', { currency }), { tone: 'ok', unique: true });
                },

                sell(currency) {
                    this.$notify(this.$t('Sell {currency}', { currency }), { tone: 'ok', unique: true });
                },

                quote(currency) {
                    this.$notify(this.$t('Quote {currency}', { currency }));
                }
            }
        }
    </script>
</ib-example>
